import { useState, useCallback, useRef, useEffect } from "react";
import "./ATT.scss";
import att from "../../data/att.json";
import customerinfo from "../../data/customerinfo";
import Map, {
  Popup,
  NavigationControl,
  FullscreenControl,
  ScaleControl,
  GeolocateControl,
  Layer,
  Source,
  MapRef,
} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { heatmapLayer } from "../../helpers/heatmapLayer";
import {
  sortVendorsByDistance,
  vendorBranchData,
} from "../../helpers/branchVendor";
import { escapeString } from "../../helpers/escapeString";
import { downloadCsv } from "../../helpers/downloadCsv";
import { Header } from "../../components/Header";
import { ActionButtons } from "../../components/ActionButtons";
import { CardButton, TextInput } from "@fsg/gui-bits";
import {
  clusterLayer,
  countLayer,
  entityLayer,
  unclusteredPointLayer,
} from "../../helpers/clusterLayer";
import { FixedSizeList as List } from "react-window";

const attData = (att as Array<any>).filter(
  (x) => typeof x["Latitude"] === "number" && typeof x["Longitude"] === "number"
);

function ATT({ publicView = false }) {
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [customerData] = useState<any>(attData);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const mapRef = useRef<MapRef>(null);
  const [filteredData, setFilteredData] = useState<any>(customerData);

  useEffect(() => {
    setFilteredData(
      customerData.filter((x: any) => {
        if (searchTerm === "") return true;
        const term = searchTerm.toLowerCase();
        return (
          String(x["Location Code"] || "")
            .toLowerCase()
            .includes(term) ||
          String(x["Street Address 1"] || "")
            .toLowerCase()
            .includes(term) ||
          String(x["City"] || "")
            .toLowerCase()
            .includes(term) ||
          String(x["State/Prov."] || "")
            .toLowerCase()
            .includes(term) ||
          String(x["Portfolio"] || "")
            .toLowerCase()
            .includes(term)
        );
      })
    );
  }, [searchTerm, customerData]);

  document.title = `FSG ${publicView ? "(Public)" : ""} -  ${
    customerinfo.att.name
  } Coverage Map`;

  const exportData = useCallback(() => {
    let csvContent =
      "Location Code,Region,Portfolio,Bu Proj Id,Street Address 1,City,State,Zip,V1,V1 Distance,V2,V2 Distance,V3,V3 Distance,V4,V4 Distance,V5,V5 Distance\n";
    customerData.forEach(function (row: any) {
      const vendors = sortVendorsByDistance(row["Latitude"], row["Longitude"]);
      const rowArray = [
        row["Location Code"],
        row["Region"],
        row["Portfolio"],
        row["Bu Proj Id"],
        row["Street Address 1"],
        row["City"],
        row["State/Prov."],
        "", // Zip not provided in att.json
        vendors[0]["Name"],
        vendors[0]["distance"].toFixed(1),
        vendors[1]["Name"],
        vendors[1]["distance"].toFixed(1),
        vendors[2]["Name"],
        vendors[2]["distance"].toFixed(1),
        vendors[3]["Name"],
        vendors[3]["distance"].toFixed(1),
        vendors[4]["Name"],
        vendors[4]["distance"].toFixed(1),
      ];
      csvContent += rowArray.map(escapeString).join(",");
      csvContent += "\n";
    });

    downloadCsv(csvContent, "att");
  }, [customerData]);

  return (
    <div>
      <Header customer={customerinfo.att.name} />
      <div className="dashboard-content">
        <ActionButtons
          customerGuid={customerinfo.att.guid}
          publicView={publicView}
          exportData={exportData}
        />

        <div className="map-container">
          <div className="site-list">
            <div className="site-search">
              <TextInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by code, address, city, state, or portfolio"
              />
            </div>
            <div className="site-list-list">
              <List
                itemCount={filteredData.length}
                itemSize={100}
                height={window.innerHeight - 240}
                width={"100%"}
              >
                {({ index, style }) => {
                  const item = filteredData[index];
                  return (
                    <div style={style}>
                      <CardButton
                        key={`${item["Bu Proj Id"]}-${item["Location Code"]}`}
                        className="site-listing"
                        onClick={() => {
                          mapRef.current?.flyTo({
                            center: [item["Longitude"], item["Latitude"]],
                            zoom: 10,
                            duration: 1000,
                          });
                        }}
                        style={{ padding: "1rem", marginRight: "0.5rem" }}
                      >
                        <h4>
                          {item["Location Code"] || item["Street Address 1"]}
                        </h4>
                        <div style={{ fontSize: "0.9rem", color: "#666" }}>
                          {item["Portfolio"]}
                        </div>
                        <address>
                          <div>{item["Street Address 1"]}</div>
                          <div>
                            {item["City"]}, {item["State/Prov."]}
                          </div>
                        </address>
                      </CardButton>
                    </div>
                  );
                }}
              </List>
            </div>
          </div>
          <Map
            ref={mapRef}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            initialViewState={{ longitude: -95.7, latitude: 37.09, zoom: 3 }}
            style={{ width: "100%", height: "80vh", marginTop: "1rem" }}
            mapStyle="mapbox://styles/mapbox/streets-v9"
            interactiveLayerIds={[
              "cluster",
              "counts",
              "unclustered-points",
              "entity-types",
            ]}
            cursor="pointer"
            onClick={(event) => {
              if (mapRef.current) {
                // Query with a larger tolerance for better click detection
                const bbox: [[number, number], [number, number]] = [
                  [event.point.x - 10, event.point.y - 10],
                  [event.point.x + 10, event.point.y + 10],
                ];

                // First check for unclustered points only
                const unclusteredFeatures = mapRef.current.queryRenderedFeatures(
                  bbox,
                  { layers: ["unclustered-points"] }
                );

                if (unclusteredFeatures && unclusteredFeatures.length > 0) {
                  const feature = unclusteredFeatures[0];
                  if (feature.properties) {
                    const locationIndex = feature.properties.index;

                    if (locationIndex !== undefined && customerData[locationIndex]) {
                      const location = customerData[locationIndex];

                      // Center the marker at top-center of the viewport
                      const map = mapRef.current.getMap();
                      map.once('moveend', () => {
                        setSelectedLocation(location);
                      });

                      mapRef.current.easeTo({
                        center: [location["Longitude"], location["Latitude"]],
                        offset: [0, -window.innerHeight * 0.25],
                        duration: 500,
                      });
                    }
                  }
                  return;
                }

                // Then check for cluster clicks
                const clusterFeatures = mapRef.current.queryRenderedFeatures(
                  event.point,
                  { layers: ["cluster"] }
                );

                if (clusterFeatures && clusterFeatures.length > 0) {
                  const clusterFeature = clusterFeatures[0] as any;
                  const clusterId = clusterFeature.properties?.cluster_id;
                  if (clusterId) {
                    const mapboxSource = mapRef.current.getSource("att") as any;
                    mapboxSource.getClusterExpansionZoom(
                      clusterId,
                      (err: any, zoom: any) => {
                        if (err || !clusterFeature) return;
                        if (!clusterFeature.geometry || !clusterFeature.geometry.coordinates)
                          return;
                        mapRef.current!.easeTo({
                          center: clusterFeature.geometry.coordinates,
                          zoom,
                          duration: 500,
                        });
                      }
                    );
                  }
                }
              }
            }}
            onDrag={() => {
              setSelectedLocation(null);
            }}
          >
            <GeolocateControl position="top-left" />
            <FullscreenControl position="top-left" />
            <NavigationControl position="top-left" />
            <ScaleControl />

            <Source
              type="geojson"
              data={{
                type: "FeatureCollection",
                features: vendorBranchData.map((item) => ({
                  type: "Feature",
                  properties: {},
                  geometry: {
                    type: "Point",
                    coordinates: [item["Longitude"], item["Latitude"]],
                  },
                })),
              }}
            >
              <Layer {...heatmapLayer}></Layer>
            </Source>

            <Source
              id="att"
              type="geojson"
              data={{
                type: "FeatureCollection",
                features: customerData.map((item: any, index: number) => ({
                  type: "Feature",
                  properties: {
                    index: index,
                  },
                  geometry: {
                    type: "Point",
                    coordinates: [item["Longitude"], item["Latitude"]],
                  },
                })),
              }}
              cluster={true}
              clusterMaxZoom={14}
              clusterRadius={50}
            >
              <Layer {...clusterLayer(["#0c0d29", "#1e90ff"])}></Layer>
              <Layer {...countLayer}></Layer>
              <Layer {...entityLayer}></Layer>
              <Layer {...unclusteredPointLayer}></Layer>
            </Source>

            {selectedLocation && (
              <Popup
                anchor="top"
                longitude={selectedLocation["Longitude"]}
                latitude={selectedLocation["Latitude"]}
                onClose={() => {
                  setSelectedLocation(null);
                }}
                style={{ width: "50vw", maxWidth: "500px" }}
              >
                <div>
                  <div className="selected-property">
                    <h1>
                      {selectedLocation["Location Code"] ||
                        selectedLocation["Street Address 1"]}
                    </h1>
                    <h2>
                      {selectedLocation["City"]},{" "}
                      {selectedLocation["State/Prov."]}
                    </h2>
                    <div style={{ fontSize: "0.8rem", color: "#999", marginTop: "0.5rem" }}>
                      Lat: {selectedLocation["Latitude"]}, Long: {selectedLocation["Longitude"]}
                    </div>
                  </div>
                  {!publicView && (
                    <div>
                      {sortVendorsByDistance(
                        selectedLocation["Latitude"],
                        selectedLocation["Longitude"]
                      ).map((item, i) => (
                        <div key={`vendor-${i}`} className="vendor-listing">
                          <div className="heading">
                            <h3>{item["Name"]}</h3>
                            <div>{item.distance.toFixed(1)} miles</div>
                          </div>
                          <address>
                            <div>
                              {item["Address"]}, {item["City"]}, {item["State"]}{" "}
                              {item["Zip Code"]}
                            </div>
                          </address>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Popup>
            )}
          </Map>
        </div>
      </div>
    </div>
  );
}

export default ATT;
